import { getHeader, uploadAudioFileDetails } from './apiOptions';
import { postDataV2 } from './apiService';
import { apiUrl, REACT_APP_WEBSOCKET_URL } from '../constants/constants';
import { getFormattedDate, getSpeakingTestFormattedDate } from '../service/utils';
import { decryptURL } from '../service/DecryptionService';
import { AxiosResponse } from 'axios';
import { Probes } from '../interface/Probe';
import {
    InsperaObject,
    SpeakCheckScreenDetailsObject,
    SpeedObject,
    settingsObject,
    TestTaker,
    PrecheckDetailsObject,
} from '../interface/interface';

// Post data to the API using the postHeader function
export const uploadAudioFile = async (
    fileBlob: Blob,
    fileFormat: string,
    fileName: string,
    testTakerDetails: TestTaker,
    insperaDetails: InsperaObject,
) => {
    try {
        const data = uploadAudioFileDetails(
            fileBlob,
            fileFormat || '.mp3',
            fileName || '',
            testTakerDetails,
            insperaDetails,
        );

        const response = await postDataV2(
            apiUrl.UPLOAD_AUDIO_FILE,
            data,
            { headers: { Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'cmst',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const getContentData = async (
    testTakerDetails: TestTaker,
    urlParams: string,
    productType: string,
    calibrationLevel: string | undefined,
): Promise<AxiosResponse<any>> => {
    try {
        const response = await postDataV2(
            `${apiUrl.GET_CONTENT_DATA}${urlParams ? '?' + urlParams : ''}`,
            {
                testTakerId: testTakerDetails.testTakerId,
                centreName: testTakerDetails.testTakerCenterName,
                langAccent: testTakerDetails.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails.testTakerSessionId,
                productType: productType,
                calibrationLevel: calibrationLevel,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'atc',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const getExtendedContentData = async (
    testTakerDetails: TestTaker,
    urlParams: string,
    productType: string,
    calibrationLevel: string | undefined,
): Promise<AxiosResponse<any>> => {
    try {
        const response = await postDataV2(
            `${apiUrl.GET_EXTENDED_CONTENT}${urlParams ? '?' + urlParams : ''}`,
            {
                testTakerId: testTakerDetails?.testTakerId,
                centreName: testTakerDetails?.testTakerCenterName,
                langAccent: testTakerDetails?.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
                ratingScore: 'NA',
                productType: productType,
                calibrationLevel: calibrationLevel,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const getLiteVersionContentData = async (testTakerDetails: TestTaker) => {
    try {
        const response = await postDataV2(
            apiUrl.GET_INT_SUITE_CONTENT,
            {
                testTakerId: testTakerDetails?.testTakerId,
                centreName: testTakerDetails?.testTakerCenterName,
                langAccent: testTakerDetails?.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const getProbes = async (testTakerDetails: TestTaker, urlParams: string, product: string = '') => {
    try {
        const response: AxiosResponse<Probes> = await postDataV2(
            `${apiUrl.GET_PROBES}${urlParams ? '?' + urlParams : ''}`,
            {
                testTakerId: testTakerDetails.testTakerId,
                centreName: testTakerDetails.testTakerCenterName,
                langAccent: testTakerDetails.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails.testTakerSessionId,
                productType: product,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'atc',
        );

        // Decrypt URLs and attach to the items
        const probesData = response.data;
        const probes = probesData.Probes;

        for (const probe of probes) {
            probe.audioPreSignUrl = await decryptURL(probe.audioPreSignUrl);
        }

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const connectToIntentAudioSocket = () => {
    let socket = new WebSocket(
        `${REACT_APP_WEBSOCKET_URL}${apiUrl.INTENT_AUDIO_DETECTION}`,
        // ["Authorization", testTaker?.testTakerAccessToken]
    );
    return socket;
};

export const getRating = () => {
    /* This part will be replaced once we integrated LEX. Till that we have to fetch Medium type
  in Part 3 as per story 859. Once lex is integarted, score will be generated through api. It will return 
  value between 0-10 .  if we receive less than 5,  medium will be played . If it is greater than or equal to
  5 , we will play  hard type question in  part 3. As of now to play medium hardcoded as 4*/
    const ratingScore = 6;
    return ratingScore;
};

export const saveSpeakingTestProgressionData = async ({
    testTakerDetails,
    playedAudioFilesDetails = [],
    status = 'Test Completed',
    fullRecordingFilePath = '',
    logId = '',
    insperaDetails = { assessmentId: '', externalId: '', partner: '', product: '' },
    speedDetails = { uploadSpeed: '', downloadSpeed: '' },
    settings = { fontSize: '', colourContrast: '' },
    precheckDetails = { selectedMicLabel: '', speakCheckDetails: { duration: 0, responseFilePath: '' } },
    partSummary = { PART_1: {}, PART_2: {}, PART_3: {} },
}: {
    testTakerDetails: TestTaker;
    playedAudioFilesDetails: any[];
    status: string;
    fullRecordingFilePath: string;
    logId: string;
    insperaDetails: InsperaObject;
    speedDetails: SpeedObject;
    settings: settingsObject;
    precheckDetails: PrecheckDetailsObject;
    partSummary: any;
}) => {
    try {
        const response: AxiosResponse<any> = await postDataV2(
            apiUrl.SPEAKING_TEST_PROGRESSION_DATA,
            {
                id: logId,
                testTakerId: testTakerDetails?.testTakerId,
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
                centerName: testTakerDetails?.testTakerCenterName,
                testDate: getSpeakingTestFormattedDate().utc,
                testCentreTimeZone: getSpeakingTestFormattedDate().localTimezone,
                language: testTakerDetails?.testTakerLanguage,
                status: status,
                playedAudioFilesDetails: playedAudioFilesDetails,
                fullAudioRecordingFilePath: fullRecordingFilePath,
                ...insperaDetails,
                uploadSpeed: speedDetails?.uploadSpeed,
                downloadSpeed: speedDetails?.downloadSpeed,
                settings: settings,
                preCheck: {
                    speakCheckScreen: precheckDetails.speakCheckDetails,
                    selectedMicrophone: precheckDetails.selectedMicLabel,
                },
                partSummary: partSummary,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'cmst',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

/**
 * @params takes a json object to send message to parent window
 */
export const sendMessageToInsperaIB = (payload: any) => {
    // Replace '*' with the iframe origin for better security.
    const finalPayload = JSON.parse(JSON.stringify(payload));
    window.parent.postMessage(finalPayload, `*`);
};

export const getCRELLAContent = async (testTakerDetails: TestTaker, examGroup: string, examSubGroup: string) => {
    try {
        const response: AxiosResponse<any> = await postDataV2(
            apiUrl.CRELLA_CONTENT,
            {
                testTakerId: testTakerDetails.testTakerId,
                centreName: testTakerDetails.testTakerCenterName,
                langAccent: testTakerDetails.testTakerLanguage,
                uniqueSessionId: testTakerDetails.testTakerSessionId,
                testTakerDate: getSpeakingTestFormattedDate().utc,
                examGroup: examGroup,
                examSubGroup: examSubGroup,
                productType: 'GT',
            },
            {
                headers: {
                    ...getHeader(),
                    Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken,
                },
            },
            'atc',
        );

        return response;
    } catch (error: any) {
        console.log('Failed fetching CRELLA content:', error);
        throw new Error(error);
    }
};

export const getCRELLAProbes = async (testTakerDetails: TestTaker, examGroup: string, examSubGroup: string) => {
    try {
        const response: AxiosResponse<any> = await postDataV2(
            apiUrl.CRELLA_PROBES,
            {
                testTakerId: testTakerDetails.testTakerId,
                centreName: testTakerDetails.testTakerCenterName,
                langAccent: testTakerDetails.testTakerLanguage,
                uniqueSessionId: testTakerDetails.testTakerSessionId,
                testTakerDate: getSpeakingTestFormattedDate().utc,
                examGroup: examGroup,
                examSubGroup: examSubGroup,
                productType: 'GT',
            },
            {
                headers: {
                    ...getHeader(),
                    Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken,
                },
            },
            'atc',
        );

        const {
            data: { Probes: probes },
        } = response;

        for (const probe of probes) {
            probe.audioPreSignUrl = await decryptURL(probe.audioPreSignUrl);
        }
        return response;
    } catch (error: any) {
        console.log('Failed fetching CRELLA probes:', error);
        throw new Error(error);
    }
};
